import { useState, useEffect, useRef, useCallback } from "react";
import { Activity, Download, RefreshCw } from "lucide-react";

// ── constants ──────────────────────────────────────────────────────
const TOOLS = [
  "db/query_execute", "db/query_read", "api/external_call",
  "files/read", "files/write", "logs/stream", "vector/search", "db/schema_view",
];
const METHODS = ["GET", "GET", "GET", "POST", "POST", "DEL"] as const;
const STATUSES = ["success", "success", "success", "success", "denied", "error"] as const;

const MON_DEVS = [
  { name: "maya",  init: "MJ", grad: "from-violet-600 to-indigo-500", token: "mcp-tok-prod-sr-7f2a" },
  { name: "james", init: "JC", grad: "from-orange-600 to-yellow-500", token: "mcp-tok-prod-sr-8c3b" },
  { name: "sarah", init: "SO", grad: "from-teal-600 to-blue-500",     token: "mcp-tok-stg-sr-2a9f"  },
  { name: "alex",  init: "AK", grad: "from-purple-600 to-pink-500",   token: "mcp-tok-stg-jr-4d1e"  },
];

const TOOL_DIST = [
  { label: "db/query_read",     value: 187, bar: "bg-indigo-500" },
  { label: "api/external_call", value: 143, bar: "bg-violet-500" },
  { label: "files/read",        value: 98,  bar: "bg-emerald-600" },
  { label: "vector/search",     value: 76,  bar: "bg-amber-500"  },
  { label: "logs/stream",       value: 54,  bar: "bg-pink-500"   },
];
const TOOL_MAX = 187;

type Status = "success" | "denied" | "error";
type Method = "GET" | "POST" | "DEL";

interface FeedRow {
  id:     number;
  time:   string;
  tool:   string;
  dev:    typeof MON_DEVS[number];
  method: Method;
  status: Status;
  lat:    number;
  isNew:  boolean;
}

// ── helpers ────────────────────────────────────────────────────────
function pad2(n: number) { return String(n).padStart(2, "0"); }
function nowStr() {
  const d = new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}.${String(d.getMilliseconds()).padStart(3, "0")}`;
}
function randItem<T>(arr: readonly T[]): T { return arr[Math.floor(Math.random() * arr.length)]; }
function randRow(id: number): FeedRow {
  return {
    id, time: nowStr(), isNew: true,
    tool:   randItem(TOOLS),
    dev:    randItem(MON_DEVS),
    method: randItem(METHODS),
    status: randItem(STATUSES),
    lat:    Math.floor(Math.random() * 290 + 8),
  };
}

// ── Sparkline ──────────────────────────────────────────────────────
function Sparkline({ data }: { data: number[] }) {
  const ref = useRef<SVGSVGElement>(null);
  useEffect(() => {
    const svg = ref.current; if (!svg) return;
    const max = Math.max(...data, 1);
    const pts = data.map((v, i) => `${(i / (data.length - 1)) * 300},${48 - (v / max) * 44}`).join(" ");
    const fill = pts + " 300,50 0,50";
    svg.innerHTML = `
      <defs><linearGradient id="mg" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="#6366f1" stop-opacity=".4"/>
        <stop offset="100%" stop-color="#6366f1" stop-opacity="0"/>
      </linearGradient></defs>
      <polygon points="${fill}" fill="url(#mg)"/>
      <polyline points="${pts}" fill="none" stroke="#6366f1" stroke-width="1.5" stroke-linejoin="round"/>
      <circle cx="300" cy="${48 - (data[data.length - 1] / max) * 44}" r="3" fill="#6366f1"/>`;
  }, [data]);
  return <svg ref={ref} viewBox="0 0 300 50" preserveAspectRatio="none" className="w-full h-12 block" />;
}

// ── StatCard ───────────────────────────────────────────────────────
function StatCard({ label, value, note, up, warn }: { label: string; value: string | number; note: string; up?: boolean; warn?: boolean }) {
  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded p-4">
      <p className="text-xs text-gray-400 mb-1">{label}</p>
      <p className="text-2xl font-semibold text-white font-mono">{value}</p>
      <p className={`text-xs mt-1 ${up ? "text-green-400" : warn ? "text-amber-400" : "text-gray-500"}`}>{note}</p>
    </div>
  );
}

// ── method badge ───────────────────────────────────────────────────
function MethodBadge({ method }: { method: Method }) {
  const cls =
    method === "GET"  ? "bg-green-900/40 text-green-400 border-green-700/30" :
    method === "POST" ? "bg-indigo-900/40 text-indigo-400 border-indigo-700/30" :
                        "bg-red-900/40 text-red-400 border-red-700/30";
  return (
    <span className={`inline-block w-9 text-center font-mono text-[9px] font-bold px-1 py-0.5 rounded border ${cls}`}>
      {method}
    </span>
  );
}

// ── status badge ───────────────────────────────────────────────────
function StatusBadge({ status }: { status: Status }) {
  if (status === "success") return <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-green-700 text-green-100">200</span>;
  if (status === "denied")  return <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-red-700 text-red-100">403</span>;
  return <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-yellow-700 text-yellow-100">500</span>;
}

// ── LiveMonitor ────────────────────────────────────────────────────
export default function MCPLiveMonitor() {
  const idRef = useRef(0);

  const [feed, setFeed] = useState<FeedRow[]>(() =>
    Array.from({ length: 20 }, () => ({ ...randRow(idRef.current++), isNew: false }))
  );
  const [spark, setSpark] = useState<number[]>(() =>
    Array.from({ length: 60 }, () => Math.floor(Math.random() * 80 + 20))
  );
  const [rpm,    setRpm]    = useState(142);
  const [latVal, setLatVal] = useState("84ms");
  const [errVal, setErrVal] = useState("0.4%");
  const [devRates, setDevRates] = useState(() =>
    MON_DEVS.map(d => ({ ...d, rate: Math.floor(Math.random() * 8 + 1) }))
  );
  const [paused, setPaused]   = useState(false);
  const [rowCount, setRowCount] = useState(0);

  const tick = useCallback(() => {
    if (paused) return;
    const row = randRow(idRef.current++);
    setRowCount(n => n + 1);

    setFeed(prev => {
      const next = [row, ...prev.slice(0, 49)];
      return next.map((r, i) => ({ ...r, isNew: i === 0 }));
    });
    setTimeout(() => {
      setFeed(prev => prev.map(r => r.id === row.id ? { ...r, isNew: false } : r));
    }, 700);

    setSpark(prev => [...prev.slice(1), Math.floor(Math.random() * 80 + 20)]);
    setRpm(Math.floor(Math.random() * 40 + 120));
    setLatVal(`${Math.floor(Math.random() * 30 + 70)}ms`);
    setErrVal(`${(Math.random() * 0.8 + 0.1).toFixed(1)}%`);
    setDevRates(MON_DEVS.map(d => ({ ...d, rate: Math.floor(Math.random() * 8 + 1) })));
  }, [paused]);

  useEffect(() => {
    const t = setInterval(tick, 1800);
    return () => clearInterval(t);
  }, [tick]);

  return (
    <div className="p-6 space-y-6 text-sm text-gray-300">

      {/* ── header ──────────────────────────────────────────────── */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-white">Live Monitor</h2>
          <p className="text-sm text-gray-400">Real-time MCP tool invocations and session activity.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs text-green-400 font-mono">
            <span className={`w-1.5 h-1.5 rounded-full bg-green-400 ${paused ? "" : "animate-pulse"}`} />
            {paused ? "PAUSED" : "LIVE"}
          </span>
          <button
            onClick={() => setPaused(p => !p)}
            className="flex items-center gap-1.5 bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 text-gray-300 px-3 py-1.5 rounded text-xs transition-colors"
          >
            <RefreshCw className="w-3 h-3" />
            {paused ? "Resume" : "Pause"}
          </button>
          <button className="flex items-center gap-1.5 bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 text-gray-300 px-3 py-1.5 rounded text-xs transition-colors">
            <Download className="w-3 h-3" /> Export
          </button>
        </div>
      </div>

      {/* ── stat cards ──────────────────────────────────────────── */}
      <div className="grid grid-cols-5 gap-4">
        <StatCard label="Req / Min"       value={rpm}     note="↑ trending up" up />
        <StatCard label="Avg Latency"     value={latVal}  note="p95: 210ms" />
        <StatCard label="Error Rate"      value={errVal}  note="3 errors / 15m" warn />
        <StatCard label="Active Sessions" value="4"       note="devs connected" />
        <StatCard label="Auth Success"    value="99.6%"   note="token valid rate" up />
      </div>

      {/* ── main layout ─────────────────────────────────────────── */}
      <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 280px" }}>

        {/* live feed */}
        <div className="overflow-x-auto border border-neutral-800 rounded">
          {/* feed header bar */}
          <div className="flex items-center justify-between px-4 py-3 bg-neutral-900 border-b border-neutral-800">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-indigo-400" />
              <span className="font-semibold text-white text-sm">Live Request Feed</span>
              <span className="text-xs text-gray-500">real-time MCP tool invocations</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-[10px] text-gray-500">{rowCount} events</span>
              <span className="flex items-center gap-1.5 text-xs text-green-400">
                <span className={`w-1.5 h-1.5 rounded-full bg-green-400 ${paused ? "" : "animate-pulse"}`} />
                live
              </span>
            </div>
          </div>

          {/* col headers */}
          <div className="grid bg-neutral-900 px-4 py-1.5 text-xs text-gray-400 border-b border-neutral-800"
            style={{ gridTemplateColumns: "110px 36px 1fr 150px 72px 72px", gap: 8 }}>
            <span>Time</span>
            <span></span>
            <span>Tool</span>
            <span>Token</span>
            <span className="text-right">Latency</span>
            <span className="text-right">Status</span>
          </div>

          {/* rows */}
          <div className="max-h-[440px] overflow-y-auto">
            {feed.map(row => (
              <div
                key={row.id}
                className={`grid items-center px-4 py-2 border-t border-neutral-800 hover:bg-neutral-800/30 transition-colors ${
                  row.isNew ? "bg-indigo-900/10" : ""
                }`}
                style={{ gridTemplateColumns: "110px 36px 1fr 150px 72px 72px", gap: 8 }}
              >
                <span className="font-mono text-[10px] text-gray-500">{row.time}</span>
                <MethodBadge method={row.method} />
                <span className="font-mono text-xs text-indigo-400 truncate">{row.tool}</span>
                <div className="flex items-center gap-1.5 min-w-0">
                  <div className={`w-5 h-5 rounded bg-gradient-to-br ${row.dev.grad} flex items-center justify-center text-[8px] font-bold flex-shrink-0`}>
                    {row.dev.init}
                  </div>
                  <span className="font-mono text-[10px] text-gray-500 truncate">{row.dev.token}</span>
                </div>
                <span className={`font-mono text-xs text-right ${
                  row.lat < 100 ? "text-green-400" : row.lat < 200 ? "text-amber-400" : "text-red-400"
                }`}>
                  {row.lat}ms
                </span>
                <span className="text-right">
                  <StatusBadge status={row.status} />
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* right panel */}
        <div className="flex flex-col gap-4">

          {/* sparkline */}
          <div className="border border-neutral-800 rounded overflow-hidden">
            <div className="px-4 py-3 bg-neutral-900 border-b border-neutral-800">
              <span className="font-semibold text-white text-sm">Throughput</span>
            </div>
            <div className="bg-neutral-900 px-4 pt-2 pb-3">
              <Sparkline data={spark} />
              <div className="flex justify-between font-mono text-[10px] text-gray-600 mt-1">
                <span>−60s</span><span>now</span>
              </div>
            </div>
          </div>

          {/* active sessions */}
          <div className="border border-neutral-800 rounded overflow-hidden">
            <div className="px-4 py-3 bg-neutral-900 border-b border-neutral-800 flex items-center justify-between">
              <span className="font-semibold text-white text-sm">Active Sessions</span>
              <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-green-700 text-green-100">
                4 online
              </span>
            </div>
            {devRates.map((d, i) => (
              <div key={d.name}
                className={`flex items-center gap-2.5 px-4 py-2.5 bg-neutral-900 ${i < devRates.length - 1 ? "border-b border-neutral-800" : ""} hover:bg-neutral-800/30 transition-colors`}>
                <div className={`w-6 h-6 rounded bg-gradient-to-br ${d.grad} flex items-center justify-center text-[9px] font-bold flex-shrink-0`}>
                  {d.init}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-200">{d.name}</p>
                  <p className="font-mono text-[10px] text-gray-500 truncate">{d.token}</p>
                </div>
                <span className="font-mono text-xs font-semibold text-green-400 flex-shrink-0">{d.rate}/s</span>
              </div>
            ))}
          </div>

          {/* tool hit rate */}
          <div className="border border-neutral-800 rounded overflow-hidden">
            <div className="px-4 py-3 bg-neutral-900 border-b border-neutral-800">
              <span className="font-semibold text-white text-sm">Tool Hit Rate</span>
            </div>
            <div className="bg-neutral-900 p-4 space-y-3">
              {TOOL_DIST.map((d, i) => (
                <div key={i}>
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-gray-300 font-mono truncate pr-2">{d.label}</span>
                    <span className="text-gray-500 flex-shrink-0">{d.value}</span>
                  </div>
                  <div className="h-1.5 bg-neutral-800 rounded-full">
                    <div className={`h-full ${d.bar} rounded-full`}
                      style={{ width: `${Math.round(d.value / TOOL_MAX * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* error breakdown */}
          <div className="border border-neutral-800 rounded overflow-hidden">
            <div className="px-4 py-3 bg-neutral-900 border-b border-neutral-800">
              <span className="font-semibold text-white text-sm">Response Breakdown</span>
            </div>
            <div className="bg-neutral-900 p-4 space-y-2.5">
              {[
                { label: "2xx Success", count: feed.filter(r => r.status === "success").length, cls: "bg-green-700 text-green-100" },
                { label: "4xx Denied",  count: feed.filter(r => r.status === "denied").length,  cls: "bg-red-700 text-red-100" },
                { label: "5xx Error",   count: feed.filter(r => r.status === "error").length,   cls: "bg-yellow-700 text-yellow-100" },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">{item.label}</span>
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${item.cls}`}>
                    {item.count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── session detail table ─────────────────────────────────── */}
      <div>
        <div className="mb-3">
          <h3 className="text-sm font-semibold text-white">Session Details</h3>
          <p className="text-xs text-gray-400">Current developer session metadata and token bindings.</p>
        </div>
        <div className="overflow-x-auto border border-neutral-800 rounded">
          <table className="min-w-full text-sm text-left text-gray-300">
            <thead className="bg-neutral-900 text-gray-400">
              <tr>
                {["Developer", "Token", "Environment", "Role", "Req / Sec", "Avg Latency", "Status"].map(h => (
                  <th key={h} className="px-4 py-2 text-xs font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {devRates.map((d, i) => (
                <tr key={i} className="border-t border-neutral-800 hover:bg-neutral-800/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded bg-gradient-to-br ${d.grad} flex items-center justify-center text-[9px] font-bold flex-shrink-0`}>
                        {d.init}
                      </div>
                      <span className="text-gray-200">{d.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{d.token}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                      d.token.includes("prod") ? "bg-red-900/40 text-red-300 border border-red-700/30" : "bg-neutral-700 text-gray-300"
                    }`}>
                      {d.token.includes("prod") ? "production" : "staging"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-indigo-900/50 text-indigo-300 border border-indigo-700/30">
                      {d.token.includes("sr") ? "senior_eng" : "junior_eng"}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-green-400">{d.rate}/s</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-300">
                    {Math.floor(Math.random() * 60 + 40)}ms
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-green-700 text-green-100">
                      Active
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}